import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getOaCreationJob } from 'api'
import { queryKeys } from 'constants/queryKeys'
import { oaCreationStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { EOaCreationJobStatus, type TOaCreationJob } from 'types'
import { toErrorMessage } from './utils'

const ACTIVE_STATUSES: EOaCreationJobStatus[] = [
  EOaCreationJobStatus.PENDING,
  EOaCreationJobStatus.RUNNING,
]

type UseOaCreationJobOptions = {
  jobId: number | undefined
  /** 指定時のみ RUNNING / PENDING のときに自動ポーリングする間隔 (ms) */
  pollIntervalMs?: number
}

type UseOaCreationJobResult = {
  job: TOaCreationJob | undefined
  loading: boolean
  error: string | undefined
  refetch: () => Promise<unknown>
}

export function useOaCreationJob({
  jobId,
  pollIntervalMs,
}: UseOaCreationJobOptions): UseOaCreationJobResult {
  const { user } = useAuth()

  const query = useQuery({
    queryKey: [queryKeys.OA_CREATION_JOB_DETAIL, user?.uid, jobId],
    enabled: Boolean(user?.uid && jobId),
    queryFn: () => getOaCreationJob(jobId as number),
    retry: false,
    refetchInterval: (q) => {
      if (pollIntervalMs == null) return false
      const data = q.state.data as TOaCreationJob | undefined
      if (!data) return false
      return ACTIVE_STATUSES.includes(data.status) ? pollIntervalMs : false
    },
  })

  const result = useMemo(
    () => ({
      job: query.data,
      loading: Boolean(user?.uid && jobId) && query.isPending,
      error: query.error
        ? toErrorMessage(query.error, oaCreationStrings.errors.loadJobsFailed)
        : undefined,
      refetch: () => query.refetch(),
    }),
    [query, user?.uid, jobId],
  )

  return result
}
